import React from 'react';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTranslation } from 'react-i18next';

import translateKey from '../../charts/translations';

const moment = require('moment');

const gridStyle = {
  opacity: 0.61,
  type: 'solid',
  color: '#8097B1',
};

const Chart = ({
  data, series, period,
}) => {
  const isMobile = useMediaQuery('(max-width:900px)');
  const { t } = useTranslation(['translation']);
  let timeInterval;
  switch (period) {
    case 'hour':
      timeInterval = 3600 * 1000; // 1 hour
      break;
    case 'day':
      timeInterval = 3600 * 24 * 1000; // 1 day
      break;
    case 'month':
      timeInterval = 3600 * 24 * 1000 * (data.length === 1 ? 1 : 30);
  }
  const options = {
    xAxis: {
      type: 'time',
      axisLabel: {
        showMinLabel: false,
        showMaxLabel: false,
        hideOverlap: true,
        formatter: value => {
          const date = new Date(value);
          switch (period) {
            case 'hour':
              return moment(date).format('D/M/YYYY HH:00');
            case 'day':
              return moment(date).format('D/M/YYYY');
            case 'month':
              return moment(date).format('M/YYYY');
            default:
              return null;
          }
        },
        textStyle: {
          color: '#666C6F',
        },
        rotate: isMobile ? 30 : 0,
      },
      splitLine: {
        lineStyle: gridStyle,
      },
      axisTick: {
        show: false,
      },
      minInterval: timeInterval,
    },
    yAxis: {
      type: 'value',
      splitLine: {
        lineStyle: gridStyle,
      },
      axisLabel: {
        textStyle: {
          color: '#666C6F',
        }
      },
      axisTick: {
        show: false,
      },
      minInterval: 1,
    },
    dataset: {
      source: data,
    },
    legend: {
      y: isMobile ? 0 : 15,
      icon: 'circle',
      data: series.map(d => ({
        name: d.name,
        textStyle: {
          color: d.itemStyle.color,
        },
      })),
      formatter: (name) => {
        return t(translateKey(name));
      },
      textStyle: {
        fontSize: 13,
      },
    },

    tooltip: {
      trigger: 'axis',
      position: (pos, _params, _dom, _rect, size) => {
        if (isMobile) {
          const obj = { top: 60 };
          obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 5;
          return obj;
        }
        const offset = pos[0] < size.viewSize[0] / 2 ? 0 : -size.contentSize[0];
        return [pos[0] + offset, '10%'];
      },
      formatter: params => {
        let showdate;
        switch (period) {
          case 'hour':
            showdate = moment(params[0].data.dateAsValue).format('D/M/YYYY HH:00');
            break;
          case 'day':
            showdate = moment(params[0].data.dateAsValue).format('D/M/YYYY');
            break;
          case 'month':
            showdate = moment(params[0].data.dateAsValue).format('M/YYYY');
            break;
          default:
            showdate = null;
        }
        /* eslint-disable indent */
        return `
          ${showdate} <br/>
          ${params.map((el) => `${el.marker} ${t(translateKey(el.seriesName))}: ${t('COMMON.INTL_NUMBER', { value: el.data[el.seriesName] })} <br/>`).join('')}
        `;
        /* eslint-enable indent */
      },
    },

    backgroundColor: 'white',

    dataZoom: [
      {
        type: 'inside',
        minValueSpan: timeInterval * 5,
      },
      {
        type: 'slider',
        start: 0,
        end: 100,
        backgroundColor: '#E2E5E6',
        dataBackground: {
          areaStyle: {
            color: '#AABAC5',
          },
        },
        handleIcon: 'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
        handleSize: '80%',
        handleStyle: {
          color: '#fff',
          shadowBlur: 3,
          shadowColor: 'rgba(0, 0, 0, 0.6)',
          shadowOffsetX: 2,
          shadowOffsetY: 2,
        },
      },
    ],

    grid: {
      top: isMobile ? 130 : 60,
      bottom: isMobile ? 100 : 60,
      left: isMobile ? '20%' : '10%',
    },

    series,
  };

  return (
    <ReactEcharts
      option={options}
      notMerge
      theme="dark"
      style={{ width: '100%', height: '400px' }}
    />
  );
};

Chart.propTypes = {
  data: PropTypes.array.isRequired,
  series: PropTypes.array.isRequired,
  period: PropTypes.string.isRequired,
  orderedKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default Chart;
